import { Component, EventEmitter, OnInit, ViewChild } from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { merge, of as observableOf } from 'rxjs';
import {
  catchError,
  debounceTime,
  map,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { User } from 'src/app/model/user';
import { FilterEnabledComponent } from '../../../helpers/filter-enabled.component';
import { RequestService } from 'src/app/services/request.service';
import { DatePipe } from '@angular/common';
import UserHelper from '../../../helpers/user-helper';
import { FilterStoreService } from 'src/app/services/filter-store.service';
import {
  PAGE_SIZE_OPTIONS,
  PageIndexService,
} from 'src/app/services/page-index-service';
import { ModalService } from 'src/app/services/modal.service';
import { AuthService } from 'src/app/services/auth.service';
import { StringHelper } from 'src/app/helpers/string-helper';

@Component({
  selector: 'app-users-overview',
  templateUrl: './users-overview.component.html',
  styleUrls: ['./users-overview.component.scss'],
})
export class UsersOverviewComponent
  extends FilterEnabledComponent
  implements OnInit
{
  public users: User[] = [];
  displayedColumns: string[] = [];
  totalItems = 0;
  loading: boolean = false;
  pageSizeOptions = PAGE_SIZE_OPTIONS;

  tableTitle: string;
  newUserLabel: string;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  reloadData: EventEmitter<any>;

  constructor(
    public datepipe: DatePipe,
    protected router: Router,
    private requestService: RequestService,
    private modalService: ModalService,
    private authService: AuthService,
    protected filterStoreService: FilterStoreService,
    protected pageIndexService: PageIndexService
  ) {
    super(
      [
        { key: 'firstName', active: false, strictEquality: false },
        { key: 'lastName', active: false, strictEquality: false },
        { key: 'userContactCompany', active: false, strictEquality: false },
      ],
      filterStoreService,
      pageIndexService
    );
  }

  ngOnInit() {
    this.tableTitle = 'Klanten';
    this.newUserLabel = 'Nieuwe klant';
    this.setDisplayColumns();

    // If the user changes the sort order, reset back to the first page.
    this.paginator.pageIndex = this.initPageIndex;
    this.paginator.pageSize = this.initPageSize;
    this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
      this.pageIndexService.setPageIndex(this.getLocalStorageKey(), 0);
    });
    this.reloadData = new EventEmitter<any>();
    this.filtersChanged.pipe(debounceTime(300)).subscribe(() => {
      this.pageIndexService.setPageIndex(this.getLocalStorageKey(), 0);
      this.paginator.pageIndex = 0;
      this.reloadData.emit();
    });
    merge(this.sort.sortChange, this.paginator.page, this.reloadData)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.loading = true;
          this.pageIndexService.setPageIndex(
            this.getLocalStorageKey(),
            this.paginator.pageIndex
          );
          this.pageIndexService.setPageSize(
            this.getLocalStorageKey(),
            this.paginator.pageSize
          );
          const userContactCompany = this.getUserContactCompanyFilter();
          const userContactCompanyParam = StringHelper.isEmpty(
            userContactCompany
          )
            ? null
            : [{ key: 'userContactCompany', value: userContactCompany }];
          return this.requestService.get(
            this.getPopulateUrl(),
            userContactCompanyParam,
            this.sort.active,
            this.sort.direction,
            this.paginator.pageSize,
            this.paginator.pageIndex,
            this.generateFilterString()
          );
        }),
        map((data) => {
          this.totalItems = Number.parseInt(data.headers.get('X-Total-Count'));
          return data.body;
        }),
        catchError(() => {
          return observableOf([]);
        })
      )
      .subscribe((data) => {
        this.users = data;
        this.loading = false;
      });
  }

  protected setDisplayColumns() {
    this.displayedColumns = [
      'firstName',
      'lastName',
      'email',
      'company',
      'lastLogin',
      'star',
    ];
  }

  createUser() {
    this.router.navigate(['/users/create'], {
      queryParams: { returnRoute: this.getReturnRoute() },
    });
  }

  editRow(row: User): void {
    this.router.navigate(['/users/edit', row.id], {
      queryParams: { returnRoute: this.getReturnRoute() },
    });
  }

  impersonate(user: User) {
    this.modalService.confirmDialog(
      'Gebruiker nabootsen',
      'Bent u zeker dat u de gebruiker ' +
        UserHelper.getName(user) +
        ' wilt nabootsen?',
      (b) => {
        if (b) {
          this.authService.impersonateUser(
            user,
            user.companies != null && user.companies.length > 0
          );
        }
      }
    );
  }

  deleteRow(row: User): void {
    this.modalService.confirmDialog(
      'Gebruiker verwijderen',
      `Bent u zeker dat u de gebruiker ${UserHelper.getName(
        row
      )} wilt verwijderen?`,
      (b) => {
        if (b) {
          this.requestService
            .delete('/users/archiveUser/' + row.id)
            .subscribe(() => this.reloadData.emit());
        }
      }
    );
  }

  formatDate(date: string) {
    if (date == null) {
      return '';
    }
    return date.substring(0, 11);
  }

  translateType(value) {
    return UserHelper.translateType(value);
  }

  protected getLocalStorageKey(): string {
    return 'UsersOverviewComponent';
  }

  protected getPopulateUrl(): string {
    return '/users/non-employees';
  }

  protected getUserContactCompanyFilter(): string {
    const userContactCompanyFilter = this.displayedFilters.find(
      (f) => f.key === 'userContactCompany'
    );
    return userContactCompanyFilter == null || !userContactCompanyFilter.active
      ? ''
      : userContactCompanyFilter.value;
  }

  protected getReturnRoute(): string {
    return '/users';
  }

  // Override
  protected generateFilterString(): string {
    let activeFilters = this.displayedFilters.filter(
      (e) => e.active && e.value && e.value.trim() != ''
    );

    let filters = [];
    activeFilters.forEach((filter) => {
      if (filter.key === 'userContactCompany') {
        return;
      }
      let prefix = filter.strictEquality || filter.startsWith ? '' : '*';
      let suffix = filter.strictEquality ? '' : '*';
      let opToken = filter.strictEquality ? encodeURIComponent('=') : ':';
      filters.push(filter.key + opToken + prefix + filter.value + suffix);
    });

    return filters.join(',');
  }
}
